import { useEffect, useMemo, useRef, useState } from 'react';
import { NestedSubAccount } from '../SubAccountListContainer';
import OrgChart from '../OrgChart/OrgChartComponent';
import OrgItem from './OrgItem/OrgItem';
import styles from './OrgChartContainer.module.scss';
import { OrgChart as D3OrgChart } from 'd3-org-chart';
import {
  AccountTransferFromResponse,
  Box,
  CircleButton,
  MaterialSymbol,
  PulloverSelect,
  SubAccountBalanceView,
  SubAccountsLists,
  UtilButton,
  withStyles,
} from 'vopay';
import { useDialog } from '@/providers/Dialog.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useClientData } from '@/providers/ClientData.provider';
import useAccountBalances from '@/hooks/useApi/useAccountBalances';
import CreateSubAccount from './CreateSubAccount/CreateSubAccount';
import AccountTransfer from './AccountTransfer/AccountTransfer';
import AccountNode from './AccountNode';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { DEFAULT_SUB_ACCOUNT_BALANCE_DISPLAY } from '@/hooks/useApi/useUserSettings';
const cn = withStyles(styles);

type OrgChartContainerProps = {
  subAccounts: NestedSubAccount[];
  originalSubAccounts: Record<string, SubAccountsLists>;
  accountBlocked: boolean;
  refresh: () => void;
};

export type ExtendedNestedSubAccount = Omit<NestedSubAccount, 'SubAccounts'> & {
  isExpanded: boolean;
  isBlurred: boolean;
  isOpen: boolean;
  accountsPages: number;
  totalAccounts: number;
  isRoot: boolean;
  level: number;
  id: string;
  parentId: string;
  nodeType: 'subAccount' | 'prevTree' | 'loadNext' | 'loadPrevious' | 'accountNode';
  linked: boolean;
  breadcrumb: string[];
  parentsIds: string[];
  selectedBalanceType: SubAccountBalanceView;
  SubAccounts?: ExtendedNestedSubAccount[];
};

const OrgChartContainer: React.FC<OrgChartContainerProps> = ({
  subAccounts,
  originalSubAccounts,
  accountBlocked,
  refresh,
}) => {
  const {
    response: { clientAccountsEnabled, AccountName, AccountID },
  } = useClientData();
  const { balances } = useAccountBalances();
  const { userSettings } = useUserSettingsProvider();
  const [displayLevel, setDisplayLevel] = useState<number>(2);
  const [showUpto, setShowUpto] = useState<number>(10);
  const [isExpand, setIsExpand] = useState<boolean>(false);
  const [displayBalance, setDisplayBalance] = useState<SubAccountBalanceView>(
    userSettings.SubAccountBalanceDisplay || DEFAULT_SUB_ACCOUNT_BALANCE_DISPLAY,
  );
  const [extendedSubAccount, setExtendedSubAccount] = useState<ExtendedNestedSubAccount>();
  const [menuOpen, setMenuOpen] = useState<boolean>(true);
  const dialog = useDialog();
  const alertDialog = useAlertDialog();
  const history = useHistory();
  const chartRef = useRef<D3OrgChart<ExtendedNestedSubAccount> | null>(null);
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.List.OrgChart' });
  const flattenSubAccounts = (subAccount: ExtendedNestedSubAccount): ExtendedNestedSubAccount[] => {
    const flatList: ExtendedNestedSubAccount[] = [{ ...subAccount, SubAccounts: [] }];
    if (subAccount.SubAccounts) {
      for (const child of subAccount.SubAccounts) {
        flatList.push(...flattenSubAccounts(child));
      }
    }
    return flatList;
  };
  const filterExpanded = (subAccount: ExtendedNestedSubAccount): ExtendedNestedSubAccount | null => {
    if (!subAccount.isExpanded) {
      return {
        ...subAccount,
        SubAccounts: [],
      };
    }

    const startIndex = (subAccount.accountsPages - 1) * showUpto;
    const endIndex = subAccount.accountsPages * showUpto;

    const total = subAccount.SubAccounts?.length || 0;
    const hasPrevious = subAccount.accountsPages > 1;
    const hasNext = total > endIndex;

    const displayedSubAccounts =
      (subAccount.SubAccounts?.slice(startIndex, endIndex)
        .map(filterExpanded)
        .filter(Boolean) as ExtendedNestedSubAccount[]) || [];

    // Add "Previous" node if applicable
    if (hasPrevious && subAccount.SubAccounts) {
      displayedSubAccounts.unshift({
        ...subAccount.SubAccounts[0], // base node for shape
        isExpanded: false,
        accountsPages: 0,
        isRoot: false,
        linked: false,
        id: `${subAccount.id}-load-previous`,
        nodeType: 'loadPrevious',
        totalAccounts: showUpto,
      });
    }

    // Add "Next" node if applicable
    if (hasNext && subAccount.SubAccounts) {
      displayedSubAccounts.push({
        ...subAccount.SubAccounts[0], // base node for shape
        isExpanded: false,
        accountsPages: 0,
        isRoot: false,
        linked: false,
        id: `${subAccount.id}-load-next`,
        nodeType: 'loadNext',
        totalAccounts: total - endIndex,
      });
    }

    return {
      ...subAccount,
      SubAccounts: displayedSubAccounts,
    };
  };
  const setPageToIncludeSubAccount = (
    node: ExtendedNestedSubAccount,
    targetId: string,
  ): [ExtendedNestedSubAccount, boolean] => {
    if (!node.SubAccounts || node.SubAccounts.length === 0) {
      const isTarget = node.id === targetId;
      return [node, isTarget];
    }

    let foundIndex = -1;
    let updatedSubAccounts = node.SubAccounts;

    // Check each subaccount
    updatedSubAccounts = node.SubAccounts.map((child, index) => {
      const [updatedChild, found] = setPageToIncludeSubAccount(child, targetId);
      if (found && foundIndex === -1) {
        foundIndex = index;
      }
      return updatedChild;
    });

    // If target was found in this node's SubAccounts, set correct page
    let accountsPages = node.accountsPages;
    if (foundIndex !== -1) {
      accountsPages = Math.floor(foundIndex / showUpto) + 1;
    }

    return [
      {
        ...node,
        accountsPages,
        SubAccounts: updatedSubAccounts,
      },
      foundIndex !== -1 || node.id === targetId,
    ];
  };

  const expandedSubAccounts = useMemo(() => {
    const filteredSubAccounts = extendedSubAccount ? filterExpanded(extendedSubAccount) : null;

    const flattenedSubAccounts = filteredSubAccounts ? flattenSubAccounts(filteredSubAccounts) : [];

    const maxLevel = Math.max(...flattenedSubAccounts.map((subAccount) => subAccount.level));
    const levelRemovedSubAccounts = flattenedSubAccounts
      .map((subAccount) => {
        if (subAccount.level <= maxLevel - displayLevel && subAccount.level !== 0 && subAccount.isExpanded) {
          return {
            ...subAccount,
            nodeType: 'prevTree',
          };
        } else if (subAccount.level <= maxLevel - displayLevel && subAccount.level !== 0 && !subAccount.isExpanded) {
          return null;
        }

        return subAccount;
      })
      .filter(Boolean) as ExtendedNestedSubAccount[];

    return levelRemovedSubAccounts;
  }, [extendedSubAccount, showUpto, displayLevel]);

  const transformSubAccount = (
    subAccount: NestedSubAccount,
    level: number,
    parentId: string,
    breadcrumb: string[],
    parentsIds: string[],
  ): ExtendedNestedSubAccount => {
    // preserve expansion info on refresh

    return {
      ...subAccount,
      isExpanded: (level < displayLevel && !!subAccount.SubAccounts?.length) || level === 0,
      accountsPages: level === 0 || level < displayLevel ? 1 : 0,

      breadcrumb: [...breadcrumb],
      parentsIds: [...parentsIds],
      linked: true,
      isRoot: level === 0,
      level,
      id: subAccount.Key,
      totalAccounts: subAccount.SubAccounts?.length || 0,
      parentId,
      nodeType: level == 0 ? 'accountNode' : 'subAccount',
      isBlurred: false,
      isOpen: false,
      selectedBalanceType: displayBalance === 'do-not-show-balances' ? 'available-balance' : displayBalance,
      SubAccounts: subAccount.SubAccounts?.map((child) =>
        transformSubAccount(
          child,
          level + 1,
          subAccount.Key,
          [...breadcrumb, subAccount.AccountName],
          [...parentsIds, subAccount.Key],
        ),
      ),
    };
  };
  const resetList = () => {
    const root = {
      LegalBusinessName: '',
      DBAName: '',

      ParentAccountName: '',
      ParentAccountID: '',
      AccountID: '',
      OriginatorName: '',
      OriginatorShortName: '',
      Email: '',
      Phone: '',
      Fax: '',
      City: '',
      Province: '',
      Country: '',
      PostalCode: '',
      Address: '',
      Address2: '',
      FlinksUrl: '',
      IsActive: false,
      AccountStatus: '',
      GCMEnabled: false,
      EFTCollectEnabled: false,
      EFTSendEnabled: false,
      USDEFTCollectEnabled: false,
      USDEFTSendEnabled: false,
      ACHFundEnabled: false,
      ACHWithdrawEnabled: false,
      SameDayACHFundEnabled: false,
      SameDayACHWithdrawEnabled: false,
      RTPWithdrawEnabled: false,
      RTRWithdrawEnabled: false,
      PushToCardEnabled: false,
      InteracMoneyRequestEnabled: false,
      InteracBulkPayoutEnabled: false,
      InboundEtransferEnabled: false,
      IQ11StandardEnabled: false,
      IQ11BoostEnabled: false,
      IQ11PlusEnabled: false,
      IQ11AdvantageEnabled: false,
      PortalViewOnly: false,
      AddUserEnabled: false,
      AutoBalanceTransferEnabled: false,
      MaxFundEFTAmount: '',
      MaxWithdrawEFTAmount: '',
      MaxGCMAmount: '',
      MaxInteracMoneyAmount: '',
      MaxInteracBulkPayoutAmount: '',
      MaxRTRWithdrawAmount: '',
      MaxPushToCardAmount: '',
      ClientAccountsEnabled: false,
      DailyLimitInboundAmount: '',
      WeeklyLimitInboundAmount: '',
      MonthlyLimitInboundAmount: '',
      MonthlyInboundTransactionLimit: '',
      DailyLimitOutboundAmount: '',
      WeeklyLimitOutboundAmount: '',
      MonthlyLimitOutboundAmount: '',
      MonthlyOutboundTransactionLimit: '',
      SubAccountType: '',
      FedwireFundEnabled: false,
      FedwireWithdrawEnabled: false,
      MaxFedwireFundAmount: '',
      MaxFedwireWithdrawAmount: '',
      TransactionServiceDefault: '',
      PayLinkEnabled: false,
      BrandingEmailEnabled: false,
      BrandingELinxEnabled: false,
      BrandingPortalEnabled: false,
      BrandingOnboardingEnabled: false,
      IsBusinessCaseSet: false,
      MaxFundMyAccountAmount: '',
      MaxWithdrawMyAccountAmount: '',
      CreditCardEnabled: false,

      IQ11Enabled: false,
      Token: '',
      Webhooks: '',
      WebhookUrl: '',
      SigningAuthorities: {},
      Balances: '',
      AccountingSoftwareIntegrationEnabled: false,
      TransactionApprovalWorkflowEnabled: false,
      Currency: '',
      AccountBalance: '',
      PendingBalance: '',
      Reserve: '',
      SecurityDeposit: '',
      RollingReserveNSF: '',
      RollingReserveReturns: '',
      AvailableImmediately: '',
      AvailableBalance: '',
      DailyLimitAmount: '',
      WeeklyLimitAmount: '',
      MonthlyLimitAmount: '',
      MaxVisaDirectAmount: '',
      MonthlyTransactionLimit: '',
      AFTEnabled: false,
      KYCLightEnabled: false,
      AccountName,
      Key: AccountID,
      SubAccounts: subAccounts,
    };
    setExtendedSubAccount(transformSubAccount(root, 0, '', [], []));
    setIsInitialState(true);
  };
  useEffect(() => {
    setMenuOpen(false);

    // remove any floating existing popover elements left by the org chart library
    setTimeout(() => {
      document.querySelectorAll('[data-popover-type="orgDetails"]').forEach((element) => {
        element.remove();
      });
    }, 300);

    resetList();
  }, [subAccounts]);

  useEffect(() => {
    return () => {
      // remove any floating existing popover elements left by the org chart library
      document.querySelectorAll('[data-popover-type="orgDetails"]').forEach((element) => {
        element.remove();
      });
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) {
      document.querySelectorAll('[data-popover-type="orgDetails"]').forEach((element) => {
        element.remove();
      });
    }
  }, [menuOpen]);

  // Update all nodes' selectedBalanceType when displayBalance changes
  useEffect(() => {
    if (!extendedSubAccount) return;

    const updateAllNodesBalanceType = (node: ExtendedNestedSubAccount): ExtendedNestedSubAccount => {
      const newBalanceType = displayBalance === 'do-not-show-balances' ? 'available-balance' : displayBalance;
      return {
        ...node,
        selectedBalanceType: newBalanceType,
        SubAccounts: node.SubAccounts?.map(updateAllNodesBalanceType),
      };
    };

    setExtendedSubAccount(updateAllNodesBalanceType(extendedSubAccount));
  }, [displayBalance]);

  const expandSubAccount = (
    subAccount: ExtendedNestedSubAccount,
    id: string,
    level: number,
    parentIds: string[],
    forceExpanded?: boolean,
  ): ExtendedNestedSubAccount => {
    if (subAccount.level === level) {
      const shouldForceExpand = typeof forceExpanded === 'boolean';
      const isTargetNode = subAccount.level === level && subAccount.id === id;
      const isExpanded =
        shouldForceExpand && isTargetNode ? forceExpanded : isTargetNode ? !subAccount.isExpanded : false;

      return {
        ...subAccount,
        isExpanded,
        accountsPages: isExpanded ? 1 : 0,
        nodeType: 'subAccount',
        SubAccounts: subAccount.SubAccounts?.map((child) =>
          expandSubAccount(child, id, level, parentIds, forceExpanded),
        ),
      };
    }
    return {
      ...subAccount,
      isExpanded:
        subAccount.level > level || (subAccount.level < level && !parentIds.includes(subAccount.id))
          ? false
          : subAccount.isExpanded,
      accountsPages: subAccount.level > level ? 0 : subAccount.accountsPages,
      SubAccounts: subAccount.SubAccounts?.map((child) => expandSubAccount(child, id, level, parentIds, forceExpanded)),
    };
  };

  const handleExpand = (id: string, level: number, parentIds: string[]) => {
    setIsInitialState(false);
    if (extendedSubAccount) {
      const updatedSubAccount = expandSubAccount(extendedSubAccount, id, level, parentIds);
      setExtendedSubAccount(updatedSubAccount);
      return updatedSubAccount;
    }
    return extendedSubAccount;
  };
  const changeMenuState = (
    subAccount: ExtendedNestedSubAccount,
    id: string,
    open: boolean,
  ): ExtendedNestedSubAccount => {
    return {
      ...subAccount,
      isBlurred: open ? subAccount.id !== id : false,
      isOpen: subAccount.id === id ? open : false,
      SubAccounts: subAccount.SubAccounts?.map((child) => changeMenuState(child, id, open)),
    };
  };

  const handleMenuSateChange = (id: string, open: boolean) => {
    if (extendedSubAccount) {
      const updatedSubAccount = changeMenuState(extendedSubAccount, id, open);
      setExtendedSubAccount(updatedSubAccount);
      return updatedSubAccount;
    }
    return extendedSubAccount;
  };
  const closeAllMenus = (subAccount: ExtendedNestedSubAccount): ExtendedNestedSubAccount => {
    return {
      ...subAccount,
      isBlurred: false,
      isOpen: false,
      SubAccounts: subAccount.SubAccounts?.map((child) => closeAllMenus(child)),
    };
  };

  const loadMore = (
    subAccount: ExtendedNestedSubAccount,
    level: number,
    direction: 'next' | 'prev',
  ): ExtendedNestedSubAccount => {
    if (subAccount.level === level - 1 && subAccount.isExpanded) {
      return {
        ...subAccount,
        accountsPages: direction === 'next' ? subAccount.accountsPages + 1 : subAccount.accountsPages - 1,
      };
    }
    return {
      ...subAccount,
      SubAccounts: subAccount.SubAccounts?.map((child) => loadMore(child, level, direction)),
    };
  };

  const handleLoadNext = (level: number) => {
    if (extendedSubAccount) {
      setExtendedSubAccount(loadMore(extendedSubAccount, level, 'next'));
    }
  };
  const handleLoadPrev = (level: number) => {
    if (extendedSubAccount) {
      setExtendedSubAccount(loadMore(extendedSubAccount, level, 'prev'));
    }
  };
  async function handleOpenCreateSubAccount(node: ExtendedNestedSubAccount) {
    const resp = await dialog.open<boolean>(({ onCancel, onConfirm }) => (
      <CreateSubAccount
        node={node}
        onClose={onCancel}
        onSuccess={() => {
          onConfirm(true);
        }}
      />
    ));
    if (resp) {
      refresh();
      alertDialog.success(t('SuccessMessage.title'), t('SuccessMessage.message'));
    }
  }
  async function handleOpenAccountTransfer(node: ExtendedNestedSubAccount) {
    await dialog.open<AccountTransferFromResponse>(({ onCancel }) => (
      <AccountTransfer node={node} onClose={onCancel} subAccounts={originalSubAccounts} />
    ));
  }

  const handleBalanceTypeChange = (nodeId: string, balanceType: SubAccountBalanceView) => {
    if (!extendedSubAccount) return;
    const updateNodeBalanceType = (node: ExtendedNestedSubAccount): ExtendedNestedSubAccount => {
      if (node.id === nodeId) {
        return { ...node, selectedBalanceType: balanceType };
      }
      return {
        ...node,
        SubAccounts: node.SubAccounts?.map(updateNodeBalanceType),
      };
    };
    setExtendedSubAccount(updateNodeBalanceType(extendedSubAccount));
  };

  const [isInitialState, setIsInitialState] = useState(true);

  const subListItems = useMemo(() => {
    const subAccounts = extendedSubAccount?.SubAccounts || [];

    if (isInitialState) {
      return subAccounts.map((subAccount) => ({
        ...subAccount,
        isExpanded: false,
      }));
    }

    return subAccounts;
  }, [extendedSubAccount?.SubAccounts, isInitialState]);

  const maxOpenDepth = useMemo(() => {
    const findMaxOpenDepth = (subAccount: ExtendedNestedSubAccount, currentDepth: number): number => {
      if (!subAccount.isExpanded) {
        return currentDepth;
      }
      return Math.max(
        currentDepth,
        ...(subAccount.SubAccounts?.map((child) => findMaxOpenDepth(child, currentDepth + 1)) || []),
      );
    };

    return extendedSubAccount ? findMaxOpenDepth(extendedSubAccount, 0) : 0;
  }, [extendedSubAccount]);

  const SubAccountItem = ({
    subAccount,
    onSelect,
    onToggleExpand,
    lastItem = false,
  }: {
    subAccount: ExtendedNestedSubAccount;
    onSelect: (account: ExtendedNestedSubAccount) => void;
    onToggleExpand: (account: ExtendedNestedSubAccount) => void;
    lastItem?: boolean;
  }) => {
    const hasChildren = Array.isArray(subAccount.SubAccounts) && subAccount.SubAccounts.length > 0;

    return (
      <>
        <li
          className={cn('subAccountsListItem', subAccount.isExpanded && 'active', lastItem && 'lastItem')}
          onClick={() => {
            if (hasChildren) {
              onToggleExpand(subAccount);
            } else {
              onSelect(subAccount);
            }
          }}
        >
          <span className={cn('subAccountName')}>
            {maxOpenDepth == subAccount.level && subAccount.level !== 1 && (
              <MaterialSymbol icon="fiber_manual_record" fillAmount={1} size="14px" />
            )}
            {((subAccount.level !== 1 && maxOpenDepth != subAccount.level) ||
              (subAccount.isExpanded && subAccount.level === 1)) && (
              <MaterialSymbol icon="remove" color="#2D2D2D80" size="16px" />
            )}
            {subAccount.AccountName}
          </span>
          {hasChildren && (
            <MaterialSymbol icon={subAccount.isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} size="24px" />
          )}
        </li>
        {hasChildren && subAccount.isExpanded && (
          <>
            {subAccount.SubAccounts?.map((child, index, array) => (
              <SubAccountItem
                key={child.Key}
                subAccount={child}
                onSelect={onSelect}
                onToggleExpand={onToggleExpand}
                lastItem={index === array.length - 1}
              />
            ))}
          </>
        )}
      </>
    );
  };

  return (
    <div className={cn('container', isExpand && 'expanded')}>
      {!isExpand && (
        <div className={cn('subAccountsListContainer')}>
          <ul data-testid="orgChartContainer-subAccountsList" className={cn('subAccountsList', 'scrollbar')}>
            {subListItems.map((subAccount) => (
              <SubAccountItem
                key={subAccount.Key}
                subAccount={subAccount}
                onSelect={(subAccount) => {
                  extendedSubAccount &&
                    setExtendedSubAccount(
                      closeAllMenus(setPageToIncludeSubAccount(extendedSubAccount, subAccount.id)[0]),
                    );
                  setMenuOpen(false);
                  // remove any floating existing popover elements left by the org chart library
                  setTimeout(() => {
                    document.querySelectorAll('[data-popover-type="orgDetails"]').forEach((element) => {
                      element.remove();
                    });
                    if (chartRef.current) {
                      chartRef.current.setCentered(subAccount.id).render();
                    }
                  }, 700);
                }}
                onToggleExpand={(subAccount) => {
                  extendedSubAccount &&
                    setExtendedSubAccount(
                      setPageToIncludeSubAccount(
                        closeAllMenus(
                          expandSubAccount(
                            extendedSubAccount,
                            subAccount.id,
                            subAccount.level,
                            subAccount.parentsIds,
                            isInitialState ? true : undefined,
                          ),
                        ),
                        subAccount.id,
                      )[0],
                    );
                  setIsInitialState(false);

                  setMenuOpen(false);

                  // remove any floating existing popover elements left by the org chart library
                  setTimeout(() => {
                    document.querySelectorAll('[data-popover-type="orgDetails"]').forEach((element) => {
                      element.remove();
                    });
                    if (chartRef.current) {
                      chartRef.current.setCentered(subAccount.id).render();
                    }
                  }, 700);
                }}
              />
            ))}
          </ul>
        </div>
      )}
      <div className={cn('orgChartAreaContainer')}>
        <Box flx="row" jc="sb">
          <Box gapSize="16px" ai="center" className={cn('orgChartHeader')}>
            <Box gapSize="4px" ai="center">
              <div>{t('DisplayLevels.prefix')}</div>
              <PulloverSelect value={String(displayLevel)} onChange={(value) => setDisplayLevel(Number(value))}>
                <PulloverSelect.Option value="1">{t('DisplayLevels.suffix', { level: '1' })}</PulloverSelect.Option>
                <PulloverSelect.Option value="2">{t('DisplayLevels.suffixPlur', { level: '2' })}</PulloverSelect.Option>
                <PulloverSelect.Option value="3">{t('DisplayLevels.suffixPlur', { level: '3' })}</PulloverSelect.Option>
                <PulloverSelect.Option value="4">{t('DisplayLevels.suffixPlur', { level: '4' })}</PulloverSelect.Option>
                <PulloverSelect.Option value="5">{t('DisplayLevels.suffixPlur', { level: '5' })}</PulloverSelect.Option>
                <PulloverSelect.Option value="6">{t('DisplayLevels.suffixPlur', { level: '6' })}</PulloverSelect.Option>
                <PulloverSelect.Option value="7">{t('DisplayLevels.suffixPlur', { level: '7' })}</PulloverSelect.Option>
                <PulloverSelect.Option value="8">{t('DisplayLevels.suffixPlur', { level: '8' })}</PulloverSelect.Option>
                <PulloverSelect.Option value="9">{t('DisplayLevels.suffixPlur', { level: '9' })}</PulloverSelect.Option>
                <PulloverSelect.Option value="10">
                  {t('DisplayLevels.suffixPlur', { level: '10' })}
                </PulloverSelect.Option>
              </PulloverSelect>
              <div></div>
            </Box>
            <Box gapSize="4px" ai="center">
              <div>{t('ShowUpToSubAccounts.prefix')}</div>
              <PulloverSelect value={String(showUpto)} onChange={(value) => setShowUpto(Number(value))}>
                <PulloverSelect.Option value="5">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '5' })}
                </PulloverSelect.Option>
                <PulloverSelect.Option value="10">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '10' })}
                </PulloverSelect.Option>
                <PulloverSelect.Option value="15">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '15' })}
                </PulloverSelect.Option>
                <PulloverSelect.Option value="20">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '20' })}
                </PulloverSelect.Option>
                <PulloverSelect.Option value="25">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '25' })}
                </PulloverSelect.Option>
                <PulloverSelect.Option value="30">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '30' })}
                </PulloverSelect.Option>
                <PulloverSelect.Option value="40">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '40' })}
                </PulloverSelect.Option>
                <PulloverSelect.Option value="50">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '50' })}
                </PulloverSelect.Option>
                <PulloverSelect.Option value="75">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '75' })}
                </PulloverSelect.Option>
                <PulloverSelect.Option value="100">
                  {t('ShowUpToSubAccounts.suffix', { subAccounts: '100' })}
                </PulloverSelect.Option>
              </PulloverSelect>
            </Box>
            <Box gapSize="4px" ai="center">
              <div>{t('BalancesLabel.label', { defaultValue: 'Balances' })}</div>
              <PulloverSelect value={displayBalance} onChange={(value) => setDisplayBalance(value)}>
                <PulloverSelect.Option value="do-not-show-balances">
                  {t('BalancesLabel.doNotShowBalances', { defaultValue: "Don't Show Balances" })}
                </PulloverSelect.Option>
                <PulloverSelect.Option value="roll-up-balance">
                  {t('BalancesLabel.rollUpBalance', { defaultValue: 'Available Roll-Up Balance' })}
                </PulloverSelect.Option>
                <PulloverSelect.Option value="available-balance">
                  {t('BalancesLabel.availableBalance', { defaultValue: 'Available Balance' })}
                </PulloverSelect.Option>
              </PulloverSelect>
            </Box>
          </Box>
          <UtilButton
            onClick={() => {
              resetList();
            }}
            iconLeft="replay"
          >
            {t('resetButton')}
          </UtilButton>
        </Box>

        <div data-testid="orgChartContainer" className={cn('orgChartContainer')}>
          {useMemo(
            () => (
              <OrgChart
                data={expandedSubAccounts}
                nodeHeight={(node) => {
                  if (node.nodeType === 'prevTree') {
                    return 32;
                  }

                  return displayBalance === 'do-not-show-balances' ? 98 : 126;
                }}
                nodeWidth={(node) => {
                  if (node.nodeType === 'prevTree') {
                    return 73;
                  }
                  return 180;
                }}
                chartRef={chartRef}
                siblingsMargin={33}
                childrenMargin={48}
                neighborMargin={33}
                onNodeClick={() => {}}
                renderComponent={(node) => {
                  if (node.nodeType === 'accountNode') {
                    return (
                      <AccountNode
                        balances={balances}
                        displayBalance={displayBalance}
                        accountName={node.AccountName}
                        isBlurred={node.isBlurred}
                      />
                    );
                  } else if (node.nodeType === 'loadNext') {
                    return (
                      <div className={cn('loadMoreContainer')}>
                        <button className={cn('loadMore')} type="button" onClick={() => handleLoadNext(node.level)}>
                          <MaterialSymbol icon="cached" />
                          {t('loadNextButton', {
                            nodes: Math.min(node.totalAccounts - node.accountsPages * showUpto, showUpto),
                          })}
                        </button>
                      </div>
                    );
                  } else if (node.nodeType === 'loadPrevious') {
                    return (
                      <div className={cn('loadMoreContainer')}>
                        <button className={cn('loadMore')} type="button" onClick={() => handleLoadPrev(node.level)}>
                          <MaterialSymbol icon="cached" />
                          {t('loadPrevButton', {
                            nodes: Math.min(node.totalAccounts - node.accountsPages * showUpto, showUpto),
                          })}
                        </button>
                      </div>
                    );
                  } else if (node.nodeType === 'prevTree') {
                    return (
                      <button
                        className={cn('prevTree')}
                        type="button"
                        onClick={() => handleExpand(node.id, node.level, node.parentsIds)}
                      >
                        <MaterialSymbol icon="more_horiz" />
                      </button>
                    );
                  } else {
                    return (
                      <OrgItem
                        node={node}
                        clientAccountsEnabled={clientAccountsEnabled}
                        accountBlocked={accountBlocked}
                        onLocationChange={(location, state) => {
                          history.push(location, state);
                        }}
                        onToggleExpand={(node) => handleExpand(node.id, node.level, node.parentsIds)}
                        onChangeMenuState={(open, node) => {
                          handleMenuSateChange(node.id, open);
                          setMenuOpen(open);
                        }}
                        openCreateSubAccount={handleOpenCreateSubAccount}
                        openAccountTransfer={handleOpenAccountTransfer}
                        displayBalance={displayBalance}
                        onBalanceTypeChange={handleBalanceTypeChange}
                      />
                    );
                  }
                }}
              />
            ),
            [expandedSubAccounts, balances, accountBlocked],
          )}
          {menuOpen && <div className={cn('overlay')} />}
        </div>
      </div>
      <div className={cn('expandButtonContainer')}>
        {isExpand && (
          <CircleButton
            colorScheme="action"
            onClick={() => {
              setIsExpand(false);
            }}
            icon="zoom_in_map"
          />
        )}
        {!isExpand && (
          <CircleButton
            colorScheme="action"
            onClick={() => {
              setIsExpand(true);
            }}
            icon="zoom_out_map"
          />
        )}
      </div>
    </div>
  );
};

export default OrgChartContainer;
